import { supabase } from "@/lib/supabase";

export interface UsageMetadata {
  action?: string;
  document_type?: string;
  jurisdiction?: string;
  case_type?: string;
  topic?: string;
  template?: string;
  debt_type?: string;
  issue?: string;
  technology?: string;
  mark?: string;
  regulation?: string;
  [key: string]: unknown;
}

export class UsageTracker {
  /**
   * Track feature usage for dashboard statistics
   */
  static async trackUsage(
    userId: string,
    featureId: string,
    metadata?: UsageMetadata
  ): Promise<void> {
    try {
      // UsageTracker: Attempting to track usage

      // Check if user is authenticated
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("UsageTracker: Session error:", sessionError);
        return;
      }

      if (!session?.user) {
        console.error("UsageTracker: No active session found");
        return;
      }

      // UsageTracker: Session found

      const { data, error } = await supabase
        .from("feature_usage")
        .insert([
          {
            user_id: userId,
            feature_id: featureId,
            used_at: new Date().toISOString(),
            metadata: metadata || {},
          },
        ])
        .select();

      if (error) {
        console.error("UsageTracker: Database error:", error);
        console.error("UsageTracker: Error details:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
      } else {
      }
    } catch (error) {
      console.error("UsageTracker: Unexpected error in trackUsage:", error);
    }
  }

  /**
   * Track contract review usage
   */
  static async trackContractReview(
    userId: string,
    action: string,
    documentType: string
  ): Promise<void> {
    await this.trackUsage(userId, "contract-review", {
      action,
      document_type: documentType,
    });
  }

  /**
   * Track legal research usage
   */
  static async trackLegalResearch(
    userId: string,
    action: string,
    jurisdiction?: string,
    caseType?: string
  ): Promise<void> {
    await this.trackUsage(userId, "legal-research", {
      action,
      jurisdiction,
      case_type: caseType,
    });
  }

  /**
   * Track legal chatbot usage
   */
  static async trackLegalChatbot(
    userId: string,
    action: string,
    topic?: string,
    caseType?: string
  ): Promise<void> {
    await this.trackUsage(userId, "legal-chatbot", {
      action,
      topic,
      case_type: caseType,
    });
  }

  /**
   * Track document generator usage
   */
  static async trackDocumentGenerator(
    userId: string,
    action: string,
    template: string
  ): Promise<void> {
    await this.trackUsage(userId, "document-generator", {
      action,
      template,
    });
  }

  /**
   * Track consumer assistant usage
   */
  static async trackConsumerAssistant(
    userId: string,
    action: string,
    issue: string
  ): Promise<void> {
    await this.trackUsage(userId, "consumer-assistant", {
      action,
      issue,
    });
  }

  /**
   * Track debt collection usage
   */
  static async trackDebtCollection(
    userId: string,
    action: string,
    debtType: string
  ): Promise<void> {
    await this.trackUsage(userId, "debt-collection", {
      action,
      debt_type: debtType,
    });
  }

  /**
   * Track IP assistant usage
   */
  static async trackIPAssistant(
    userId: string,
    action: string,
    technology?: string,
    mark?: string
  ): Promise<void> {
    await this.trackUsage(userId, "ip-assistant", {
      action,
      technology,
      mark,
    });
  }

  /**
   * Track litigation predictor usage
   */
  static async trackLitigationPredictor(
    userId: string,
    action: string,
    caseType: string
  ): Promise<void> {
    await this.trackUsage(userId, "litigation-predictor", {
      action,
      case_type: caseType,
    });
  }

  /**
   * Track compliance monitor usage
   */
  static async trackComplianceMonitor(
    userId: string,
    action: string,
    regulation: string
  ): Promise<void> {
    await this.trackUsage(userId, "compliance-monitor", {
      action,
      regulation,
    });
  }

  /**
   * Track whistleblower usage
   */
  static async trackWhistleblower(
    userId: string,
    action: string,
    reportType: string
  ): Promise<void> {
    await this.trackUsage(userId, "whistleblower", {
      action,
      report_type: reportType,
    });
  }

  static async trackProcessUpdate(
    userId: string,
    action: string,
    caseNumber: string
  ): Promise<void> {
    await this.trackUsage(userId, "process-update", {
      action,
      case_number: caseNumber,
    });
  }
}
